'use client'

import React, {
	ForwardedRef,
	ReactNode,
	useState,
	useRef,
	useEffect,
	forwardRef,
	useCallback,
	SyntheticEvent,
	RefObject,
	useImperativeHandle,
} from 'react'
import {cx} from '@local/utils/cx'
import {InView} from 'react-intersection-observer'

import {useDevice, useDebouncedCallback, useResizeObserver} from '@local/hooks'
import {devices} from '@local/config/dist/theme'
import {SanityImage} from '@local/ui/components'

import type {MediaVideoSrcs} from '@local/types/media'
import {DeviceEnum, SanityImageType} from '@local/types'
import MuteIcon from '@local/assets/svgs/icon-mute.svg'
import UnmuteIcon from '@local/assets/svgs/icon-unmute.svg'

export interface VideoLoopProps extends React.HTMLAttributes<HTMLVideoElement> {
	srcs: MediaVideoSrcs | []
	children?: ReactNode
	poster?: SanityImageType
	posterDesktopWidth?: number
	posterMobileWidth?: number
	desktopOnly?: boolean
	ratio?: number
	ratioMobile?: number
	forceRatio?: boolean
	className?: string
	posterClassName?: string
	preload?: string
	loop?: boolean
	muted?: boolean,
	hasUnmuteButton?: boolean
	fill?: boolean,
	playing?: boolean
	priority?: boolean
	position?: string
	videoRef?: RefObject<HTMLVideoElement>
	onPosterComplete?: (result: {
		naturalWidth: number
		naturalHeight: number
	}) => void
	onMetadataLoaded?: (
		e: SyntheticEvent<HTMLVideoElement, Event>
	) => void | undefined
}

export const VideoLoop = forwardRef(
	(
		{
			srcs = [],
			poster,
			posterDesktopWidth,
			posterMobileWidth,
			desktopOnly = false,
			ratio,
			ratioMobile,
			forceRatio,
			className,
			posterClassName,
			children,
			playing = true,
			loop = true,
			muted = true,
			hasUnmuteButton = false,
			fill,
			priority = false,
			position = 'relative',
			preload = 'metadata',
			onPosterComplete,
			onMetadataLoaded,
			onEnded,
			videoRef,
			...rest
		}: VideoLoopProps,
		ref?: ForwardedRef<HTMLElement>,
	) => {
		const localRef = useRef<HTMLElement | null>(null)
		const localVideoRef = useRef<HTMLVideoElement | null>(null)
		const [isMetadataLoaded, setIsMetadataLoaded] = useState(false)
		const [src, setSrc] = useState<string | null>(null)
		const [isMuted, setIsMuted] = useState(muted)
		const _posterMobileWidth = posterMobileWidth || devices.mobile.mockupWidth
		const _posterDesktopWidth = posterDesktopWidth || devices.desktop.mockupWidth

		const device = useDevice(true)
		const entries = srcs
			.sort((a, b) => a.width - b.width)
			.map(e => [e.width, e.url])
		if (entries.length) entries[entries.length - 1][0] = Infinity

		const setRefs = useCallback((node: HTMLElement) => {
			localRef.current = node
			if (ref) {
				ref.current = node
			}
		}, [])

		const setVideoRefs = useCallback((node: InView) => {
			if (node) {
				localVideoRef.current = node.node as HTMLVideoElement
				if (videoRef) videoRef.current = localVideoRef.current
			}
		}, [])

		const play = () => {
			if (playing && localVideoRef.current) {
				const p = localVideoRef.current.play()
				if (p !== undefined) {
					p.catch((error) => {
						console.warn('video', error)
					})
				}
			}
		}

		const pause = () => {
			if (localVideoRef.current && !localVideoRef.current.paused) {
				const p = localVideoRef.current.pause()
				if (p !== undefined) {
					p.catch((error: any) => {
						console.warn('video', error)
					})
				}
			}
		}

		const restart = () => {
			if (localVideoRef.current) {
				localVideoRef.current.currentTime = 0
			}
		}

		const handleInView = (
			inView: boolean,
			entry,
		) => {
			const target: HTMLVideoElement = entry.target as HTMLVideoElement
			target.isInView = inView

			if (inView && playing) {
				play()
			} else {
				pause()
				target.currentTime = 0
			}
		}

		const handleLoadedMetadata = (e: SyntheticEvent<HTMLVideoElement, Event>) => {
			if (onMetadataLoaded) onMetadataLoaded(e)
			setIsMetadataLoaded(true)
			const target: HTMLVideoElement = e.currentTarget
			if (playing && target.paused && target.isInView) {
				play()
			}
		}

		useEffect(() => {
			if (!localVideoRef.current?.isInView) return
			if (playing) play()
			else pause()
		}, [playing])

		useResizeObserver(
			localRef,
			useDebouncedCallback((entry: ResizeObserverEntry) => {
				if (localRef.current && srcs?.length) {
					if (
						!desktopOnly ||
						(device?.current &&
						(device.current === DeviceEnum.desktop ||
						device.current === DeviceEnum.tablet))
					) {
						const source = entries.filter(e => {
							return entry.contentRect.width < e[0]
						})
						setSrc(source[0][1])
					} else {
						setSrc(null)
					}
				}
			}),
		)

		useImperativeHandle(ref, () => {
			return {
				play,
				pause,
				restart,
			}
		})

		const toggleSound = () => {
			setIsMuted(!isMuted)
		}
		return (
			<div ref={setRefs} className={cx(position, className)}>
				{/* {src && ( */}
				{hasUnmuteButton &&
					<button onClick={toggleSound} className='absolute right-20 bottom-30 z-9'>	{isMuted ? <UnmuteIcon className='w-20 h-20 mr-4 fill-grey-1' alt='Play Icon' aria-hidden /> : <MuteIcon className='w-20 h-20 mr-4 fill-grey-1' alt='Play Icon' aria-hidden />}</button>}
				<InView
					as='video'
					src={src}
					ref={setVideoRefs}
					onChange={handleInView}
					className={cx(
						'absolute w-full top-0 left-0 z-1 opacity-0 transition-opacity ease-quart-out',
						poster && ratio ? 'duration-100' : 'duration-500',
						isMetadataLoaded && 'opacity-100',
						fill && 'object-cover h-full',
					)}
					preload={preload}
					loop={loop}
					muted={isMuted}
					playsInline
					onLoadedMetadata={handleLoadedMetadata}
					onEnded={onEnded}
					{...rest}>
					{children}

				</InView>
				{/* )} */}
				{poster && (
					<div className={cx(ratio && 'absolute z-0 inset-0')}>
						<SanityImage
							image={poster}
							desktopOnly={Boolean(ratioMobile)}
							desktopWidth={_posterDesktopWidth}
							mobileWidth={_posterMobileWidth}
							onLoad={onPosterComplete}
							width={ratio}
							height={ratio ? 1 : undefined}
							fill={fill}
							priority={!ratioMobile && priority}
							className={cx('w-full', fill && 'object-cover h-full', posterClassName)} />
						{ratioMobile && (
							<SanityImage
								image={poster}
								mobileOnly
								mobileWidth={_posterMobileWidth}
								onLoad={onPosterComplete}
								width={ratioMobile}
								height={ratio ? 1 : undefined}
								fill={fill}
								priority={!ratioMobile && priority}
								className={cx('w-full', fill && 'object-cover h-full', posterClassName)} />
						)}
					</div>
				)}
				{(!fill || forceRatio) && ratio && (
					<div
						style={{
							['--ratio' as string]: ratio / 100,
							['--ratioMobile' as string]: ratioMobile && ratioMobile / 100,
						}}
						className={cx('w-full pb-[calc(1%/var(--ratio))]', ratioMobile && 'lg-max:pb-[calc(1%/var(--ratioMobile))]')} />
				)}
			</div>
		)
	},
)
VideoLoop.displayName = 'VideoLoop'
